import type { MiniAppSessionResponse } from "../../api/types.js";
import { fetchStorefrontHome } from "../../api/client.js";
import { messages, segmentLabel } from "../../i18n/messages.js";
import { AppButton } from "../ui/AppButton.js";
import { ManagerCabinetView } from "../manager/ManagerCabinetView.js";
import { useEffect, useState } from "react";

interface ProfileViewProps {
  session: MiniAppSessionResponse;
}

function SoonSection({ title, soonLabel }: { title: string; soonLabel: string }) {
  return (
    <div className="rounded-[16px] border border-black/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700 dark:bg-orange-950/40 dark:text-orange-200">
          {soonLabel}
        </span>
      </div>
    </div>
  );
}

export function ProfileView({ session }: ProfileViewProps) {
  const [showManagerCabinet, setShowManagerCabinet] = useState(false);
  const [storeLinks, setStoreLinks] = useState<{
    instagram?: string;
    tiktok?: string;
    youtube?: string;
  } | null>(null);
  const user = session.telegram_user;
  const client = session.client;
  const profile = session.client_profile;
  const displayName =
    [client?.first_name ?? user.first_name, client?.last_name ?? user.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || user.first_name;
  const avatarUrl = user.username
    ? `https://t.me/i/userpic/320/${user.username}.jpg`
    : null;

  useEffect(() => {
    void (async () => {
      try {
        const home = await fetchStorefrontHome();
        setStoreLinks(home.links ?? null);
      } catch {
        setStoreLinks(null);
      }
    })();
  }, []);

  if (showManagerCabinet) {
    return <ManagerCabinetView onBack={() => setShowManagerCabinet(false)} />;
  }

  return (
    <section className="flex flex-1 flex-col gap-4 px-4 py-4">
      <div className="flex items-center gap-3 rounded-[18px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-black/5 text-xl font-semibold">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            displayName.slice(0, 1).toUpperCase()
          )}
        </div>
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{displayName}</h2>
          {user.username ? <p className="text-sm opacity-60">@{user.username}</p> : null}
          <p className="mt-1 text-sm">
            {messages.profile.phone}: {client?.phone?.trim() || messages.profile.phoneMissing}
          </p>
          {profile?.segment ? (
            <p className="mt-1 text-xs opacity-70">
              {messages.profile.segment}: {segmentLabel(profile.segment)}
            </p>
          ) : null}
        </div>
      </div>

      <div className="rounded-[16px] border border-black/5 p-4">
        <h3 className="text-sm font-semibold">{messages.profile.orders}</h3>
        <p className="mt-2 text-sm opacity-70">{messages.profile.ordersPlaceholder}</p>
      </div>

      <SoonSection title={messages.profile.favorites} soonLabel={messages.profile.favoritesSoon} />
      <SoonSection
        title={messages.profile.recentlyViewed}
        soonLabel={messages.profile.recentlyViewedSoon}
      />
      <SoonSection
        title={messages.profile.repeatPurchase}
        soonLabel={messages.profile.repeatPurchaseSoon}
      />
      <SoonSection
        title={messages.profile.deliveryAddresses}
        soonLabel={messages.profile.deliveryAddressesSoon}
      />
      <SoonSection title={messages.profile.loyalty} soonLabel={messages.profile.loyaltySoon} />

      {storeLinks?.instagram ? (
        <div className="rounded-[16px] border border-black/5 p-4">
          <h3 className="text-sm font-semibold">{messages.profile.social}</h3>
          <AppButton
            variant="secondary"
            className="mt-3 w-full"
            onClick={() => window.open(storeLinks.instagram!, "_blank", "noopener,noreferrer")}
          >
            Instagram
          </AppButton>
        </div>
      ) : null}

      <div className="rounded-[16px] border border-black/5 p-4">
        <h3 className="text-sm font-semibold">{messages.profile.aboutStore}</h3>
        <p className="mt-2 text-sm opacity-70">{messages.profile.aboutStoreHint}</p>
      </div>

      <div className="rounded-[16px] border border-black/5 p-4">
        <h3 className="text-sm font-semibold">{messages.profile.legal}</h3>
        <p className="mt-2 text-sm opacity-70">{messages.profile.legalHint}</p>
      </div>

      {session.manager_pricing_access ? (
        <div className="rounded-[16px] border border-orange-200 bg-orange-50 p-4">
          <h3 className="text-sm font-semibold text-orange-900">{messages.manager.title}</h3>
          <AppButton className="mt-3 w-full" onClick={() => setShowManagerCabinet(true)}>
            {messages.manager.entry}
          </AppButton>
        </div>
      ) : null}

      <div className="rounded-[16px] border border-black/5 p-4">
        <h3 className="text-sm font-semibold">{messages.profile.support}</h3>
        <p className="mt-2 text-sm opacity-70">{messages.profile.supportHint}</p>
        <AppButton
          variant="secondary"
          className="mt-3 w-full"
          onClick={() => {
            const bot = "AsiaStyleStagingTestBot";
            window.open(`https://t.me/${bot}`, "_blank", "noopener,noreferrer");
          }}
        >
          {messages.profile.support}
        </AppButton>
      </div>

      {import.meta.env.VITE_BUILD_ID ? (
        <p className="pb-4 text-center text-[10px] opacity-45">
          {messages.profile.buildIdLabel}: {import.meta.env.VITE_BUILD_ID}
        </p>
      ) : null}
    </section>
  );
}
